/**
 * The vocabulary shared by every trading surface.
 *
 * WHY THIS EXISTS. All of this lived inside Compare.tsx, which meant the only
 * way to build a second trading page was to restate it — and a restated variant
 * order or a restated "which species can actually be traded" rule drifts the
 * moment one copy is edited. The Trade Hub panes and Compare now read the same
 * definitions, so a dex added to the filter row shows up on both.
 *
 * Types only, plus pure helpers. Nothing here renders.
 */
import { FORM_GROUPS_BY_SPECIES_ID } from "@/lib/generated/pokemonForms.generated";
import { REGIONS, type Region } from "@/lib/types";

/**
 * Canonical dex-variant identity for a single trade line. Mirrors the server
 * `DexVariant` union returned by GET /api/friends/:id/compare (see
 * cloudflare/src/lib/dexBackfillMapping). The `theyHave`/`iHave` rows carry one
 * of these per matched variant, so a species owned as both a base copy and an
 * XXL copy is two distinct trade leads — each labelled with its own variant.
 */
export type CompareVariant =
  | "pokemon" | "shiny" | "lucky" | "perfect" | "purified" | "xxl" | "xxs" | "shadow"
  | "gmax" | "dynamax" | "shinyLucky";

/** 'give' = you give it to the friend; 'receive' = they give it to you. */
export type TradeDirection = "give" | "receive";

/** A server-synced planned trade with a friend (see /api/friends/:id/trade-list). */
export type PlannedTrade = {
  id: number;
  speciesId: number;
  variant: CompareVariant;
  direction: TradeDirection;
  /** Which form the trade is for (migration 0056); null = the base species. */
  formId?: string | null;
  status: "planned" | "hidden";
  createdAt: number;
};

/**
 * A single comparison/trade tile entry. `variant` is the dex axis this line is
 * surfacing (base Pokémon, Shiny, Lucky, XXL, …) so each trade line can show a
 * unified per-variant badge/logo and be filtered by variant. `isShiny` is kept
 * as a convenience for the sprite renderer (shiny artwork).
 */
export type PanelRow = {
  id: number;
  variant: CompareVariant;
  isShiny: boolean;
  /**
   * The form this lead is for, when it is form-specific (`rattata-alola`).
   *
   * /compare returns one row per (variant, species, FORM) and this was dropped
   * on the way into the panel, so an Alolan Vulpix lead drew as plain "Vulpix"
   * with no way to tell the two apart — and adding it to the trade list lost
   * the form entirely. The base form is folded to null server-side, so anything
   * still carrying a formId here is a genuinely different copy.
   */
  formId?: string | null;
  /** See LiveCompareRow.reference: a Lucky-trade possibility, not a holding. */
  reference?: boolean;
};

/** One row of the live comparison (see GET /api/friends/:id/compare). */
export type LiveCompareRow = {
  variant: string;
  speciesId: number;
  formId: string | null;
  tradeable: boolean;
  /**
   * A Lucky REFERENCE row: the friend holds the species (in a tradeable
   * variant) and you have no Lucky of it, so a lucky trade with them could
   * make one. Not something they hold as Lucky. Shown under the Lucky dex
   * pill only; under "All" these outnumbered the real leads many times over
   * (2026-09-19). Older workers omit the field, which reads as a real lead.
   */
  reference?: boolean;
};

/**
 * b19: the /compare endpoint sources from the canonical dex_records table and
 * returns one row per matched VARIANT (pokemon/shiny/lucky/perfect/shadow/
 * purified/xxl/xxs) rather than per pokemon_records row. `tradeable` is false
 * for lucky leads (a lucky cannot be traded again) so the UI can mark them as
 * reference-only.
 */
export type LiveCompare = {
  theyHave: LiveCompareRow[];
  iHave: LiveCompareRow[];
  bothHave: LiveCompareRow[];
  neitherHas: LiveCompareRow[];
  neitherHasCount: number;
  filters: unknown;
  /**
   * Pro gate: for Basic the theyHave/iHave arrays are truncated to a top-N
   * preview and `locked` is true; the *Total fields carry the real counts so
   * the client shows correct stats and a "+N with Pro" tease.
   */
  locked?: boolean;
  iHaveTotal?: number;
  theyHaveTotal?: number;
};

export type RegionFilter = Region | "all";

/**
 * Dexes shown in the friend filter, in the SAME order as the Dex page
 * (DEX_CATEGORY_ORDER). We surface every dex a trade can fill and
 * deliberately exclude Shadow (untradeable), Mega (a state, not a Pokémon)
 * and Shundo. G-Max, Dynamax and Shiny Lucky joined on 2026-09-19 (owner
 * ask): Dynamax and Gigantamax Pokémon trade in GO, so the worker diffs
 * those rows again, and a Shiny Lucky lead is a Shiny trade with a lucky
 * roll on top. Visuals come from the shared DEX_VARIANT_META. This is a
 * FIXED list — the pills always show, matching the Dex page, even when a
 * given dex has no current leads.
 */
export const VARIANT_ORDER: CompareVariant[] = [
  "pokemon", "shiny", "lucky", "xxl", "xxs", "perfect", "purified", "gmax", "dynamax", "shinyLucky",
];

export const ALLOWED_VARIANTS = new Set<string>(VARIANT_ORDER);

/**
 * A server variant string as a CompareVariant, or null when the Compare
 * surface has no tile for it.
 *
 * This used to DEFAULT TO MAIN. /compare diffs every tradeable dex variant,
 * including shinyLucky and shundo, and a Shiny Lucky Lunala the caller held
 * and the friend did not came back as a "Lunala MAIN" lead under "You have,
 * they don't" while both trainers had a plain Lunala (reported 2026-09-16).
 * A variant this surface cannot name is not a lead it can show; the caller
 * drops the row.
 */
export function normVariant(v: string): CompareVariant | null {
  const known: string[] = [
    "pokemon", "shiny", "lucky", "perfect", "purified", "xxl", "xxs", "shadow", "gmax", "dynamax", "shinyLucky",
  ];
  return known.includes(v) ? (v as CompareVariant) : null;
}

export const REGION_FILTERS: { key: RegionFilter; label: string }[] = [
  { key: "all", label: "All" },
  ...REGIONS.map((r) => ({ key: r.key as RegionFilter, label: r.label })),
];

/**
 * Trade leads a Basic account sees per direction before the Pro tease. Mirrors
 * FREE_TRADES_VISIBLE_PER_FRIEND (the mutual-trades feed) so the whole app
 * teases at the same number.
 */
export const FREE_COMPARE_LEADS = 3;

/** The form's trainer-facing name ("Alolan", "Galar"), or null for a base lead. */
export function formDisplayName(speciesId: number, formId: string | null | undefined): string | null {
  if (!formId) return null;
  const group = FORM_GROUPS_BY_SPECIES_ID.get(speciesId);
  return group?.forms.find((f) => f.formId === formId)?.displayName ?? null;
}
